"use client"

import { createContext, PropsWithChildren, useContext, useState } from "react"
import {
  offering_createAgreement,
  purchase,
  subscriptions_subscribe,
  SubscriptionSubscribeResponse,
} from "@/firebase/clientApp"
import { FirebaseContextValue, useFirebase } from "@/firebase/FirebaseProvider"
import { payments_createCustomerSession } from "@/firebase/functions/payments_createCustomerSession"
import clog from "@/util/clog"
import { useQuery } from "@tanstack/react-query"
import { useUser } from "@/context/UserContext"
import { minutes } from "@/support/times"

export type PaymentSheetContextValue = {
  clientSecret?: SubscriptionSubscribeResponse & {
    customerSessionClientSecret: string
  }
  requestPayment: ({}: {
    offeringID: string
    priceID: string
    override?: string
    termsID?: string
    offeringType: string
  }) => void
}

const PaymentSheetContext = createContext<PaymentSheetContextValue | null>(null)

type PaymentRequestData = {
  offeringID: string
  priceID: string
  override?: string
  termsID?: string
  offeringType: string
}

async function requestPaymentFetcher(
  functions: FirebaseContextValue,
  { offeringID, priceID, override, termsID, offeringType }: PaymentRequestData,
) {
  clog("requestPaymentFetcher", { offeringID, priceID, override, termsID })
  let agreementID: string | undefined

  if (termsID) {
    // TODO: handle error
    const createAgreementResponseData = await offering_createAgreement(
      functions,
      {
        offeringID,
        priceID,
      },
    )
    agreementID = createAgreementResponseData.agreementID
  }

  const data = await (
    offeringType === "SUBSCRIPTION" ? subscriptions_subscribe : purchase
  )(functions, {
    offeringID,
    priceID,
    override,
    ...(agreementID && { agreementID }),
    createCustomerSession: true,
  })

  let { customerSessionClientSecret } = data.paymentDetails
  // clog("requestPaymentFetcher", { customerSessionClientSecret })

  if (!customerSessionClientSecret) {
    const { customerSessionClientSecret: newCustomerSessionClientSecret } =
      await payments_createCustomerSession(functions, {
        currency: data.paymentDetails.currency,
      })
    customerSessionClientSecret = newCustomerSessionClientSecret
  }

  // clog("requestPaymentFetcher", { customerSessionClientSecret })

  return {
    ...data,
    customerSessionClientSecret,
  }
}

export default function PaymentSheetProvider({ children }: PropsWithChildren) {
  const { ...functions } = useFirebase()
  const { userV2: user } = useUser()

  const [paymentRequestData, setPaymentRequestData] =
    useState<null | PaymentRequestData>(null)

  const refetchInterval = minutes(60)

  // TODO: handle error
  const {
    data: clientSecret,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["offering_subscribe_or_purchase", user?.uid, paymentRequestData],
    queryFn: async () => {
      return requestPaymentFetcher(functions, paymentRequestData!)
    },
    enabled: !!(paymentRequestData && user?.uid),
    refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: refetchInterval,
  })

  return (
    <PaymentSheetContext.Provider
      value={{
        clientSecret: !isLoading ? clientSecret : undefined,
        requestPayment: setPaymentRequestData,
      }}
    >
      {children}
    </PaymentSheetContext.Provider>
  )
}

export function usePaymentSheetContext(): PaymentSheetContextValue {
  const context = useContext(PaymentSheetContext)

  if (!context) {
    throw new Error(
      `usePaymentSheetContext should be used within the PaymentSheetProvider provider!`,
    )
  }

  return context
}
